/**
 * StyleBankService — Banque hebdomadaire de combinaisons de tenues
 *
 * Récupère et met en cache local la "bible du style" générée 1× par
 * semaine en backend (Lambda outfit-style-bank-cron). Cette banque
 * contient 200+ inspirations de tenues variées qui servent à enrichir
 * OutfitEngine.generateOutfits() avec des combinaisons éprouvées.
 *
 * Cache local : 7 jours en AsyncStorage. Si la banque locale est plus
 * récente que la précédente connue, on l'utilise sans re-fetch.
 *
 * Usage :
 *   import { getStyleBank, findMatchingCombinations } from './StyleBankService';
 *   const bank = await getStyleBank({ season: 'été', temp: 22, gender: 'F' });
 *   const matches = findMatchingCombinations(myDressing, bank.combinations);
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { getAuthToken } from './authHelpers';

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';
const CACHE_KEY_PREFIX = 'outfit_style_bank_v1_';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

// ─── Construire une clé de cache stable selon les filtres ───
function cacheKey({ occasion, season, gender, temp }) {
  const parts = [
    occasion || 'all',
    season || 'all',
    gender || 'all',
    temp != null ? `t${Math.round(temp / 5) * 5}` : 'tall', // bucket par tranches de 5°C
  ];
  return CACHE_KEY_PREFIX + parts.join('_');
}

/**
 * Récupère la banque, depuis cache si frais ou depuis API sinon.
 * @param {object} opts { occasion, season, gender, temp, forceRefresh }
 * @returns {Promise<{ combinations: Array, week_of: string|null, fromCache: boolean }>}
 */
export async function getStyleBank(opts = {}) {
  const key = cacheKey(opts);

  // v1.12.398 — Cache localStorage retiré (quota error sur web).
  // Les banks sont maintenant chargées server-side (worker AWS via /admin/banks-bundle)
  // et utilisées dans le scoring backend. Côté client on fetch direct sans cache.
  // Note: la variable `key` est gardée pour cohérence d'API (loggué en debug).

  try {
    const params = new URLSearchParams();
    if (opts.occasion) params.set('occasion', opts.occasion);
    if (opts.season)   params.set('season', opts.season);
    if (opts.gender)   params.set('gender', opts.gender);
    if (Number.isFinite(opts.temp)) params.set('temp', String(opts.temp));
    if (opts.limit)    params.set('limit', String(opts.limit));

    const url = `${API_URL}/style-combinations${params.toString() ? '?' + params.toString() : ''}`;
    // v1.12.686 — /style-combinations exige un token Cognito VALIDE (authorizer
    // API Gateway). getAuthToken() ne refresh PAS → si expiré → null → 401.
    // L'appelant (HomeScreen/LooksScreen) passe opts.token = getValidToken()
    // qui lui REFRESH. Fallback getAuthToken si pas fourni.
    const token = opts.token || await getAuthToken().catch(() => null);
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    console.log('[StyleBank] 🌐 fetching', url, token ? '(with auth)' : '(no auth)');
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();

    console.log(`[StyleBank] ✓ fetched ${data.combinations?.length || 0} combos (week=${data.week_of})`);
    return { ...data, fromCache: false };
  } catch (e) {
    console.warn('[StyleBank] fetch error:', e.message);
    // Plus de fallback localStorage (cache désactivé pour éviter quota error).
    return { combinations: [], week_of: null, fromCache: false, error: e.message };
  }
}

// ─── Constantes de mapping (partagées) ───

// Mapping combo.style → clés possibles dans ai_metadata.styleScores
const STYLE_KEY_MAP = {
  'chic':         ['chic', 'élégant', 'elegant'],
  'élégant':      ['chic', 'élégant', 'elegant'],
  'classique':    ['classique', 'minimaliste', 'chic'],
  'casual':       ['casual', 'décontracté'],
  'décontracté':  ['casual', 'décontracté'],
  'sport':        ['sport', 'sportif'],
  'sportif':      ['sport', 'sportif'],
  'streetwear':   ['streetwear', 'street', 'urban', 'edgy'],
  'street':       ['streetwear', 'street'],
  'urbain':       ['streetwear', 'urban'],
  'minimaliste':  ['minimaliste', 'minimal'],
  'minimal':      ['minimaliste', 'minimal'],
  'romantique':   ['romantique', 'féminin'],
  'féminin':      ['romantique', 'féminin'],
  'bohème':       ['bohème', 'boheme', 'bohemian'],
  'workwear':     ['workwear', 'business', 'preppy', 'chic'],
  'business':     ['workwear', 'business', 'chic'],
  'preppy':       ['preppy', 'workwear', 'classique'],
  'edgy':         ['edgy', 'streetwear'],
  'vintage':      ['vintage'],
};

// Mapping combo.occasion → clés possibles dans ai_metadata.occasions
const OCCASION_KEY_MAP = {
  'casual':    ['quotidien', 'weekend'],
  'bureau':    ['bureau'],
  'soirée':    ['soirée', 'cocktail'],
  'soiree':    ['soirée', 'cocktail'],
  'sport':     ['sport'],
  'chic':      ['cocktail', 'soirée', 'cérémonie', 'bureau'],
  'date':      ['date', 'cocktail'],
  'brunch':    ['weekend', 'date', 'quotidien'],
  'plage':     ['vacances'],
  'voyage':    ['vacances', 'weekend'],
  'cérémonie': ['cérémonie', 'cocktail'],
  'ceremonie': ['cérémonie', 'cocktail'],
};

// Couleurs neutres (passe-partout)
const NEUTRAL_COLORS = new Set([
  'noir', 'black', 'blanc', 'white', 'beige', 'gris', 'grey', 'gray',
  'écru', 'ecru', 'crème', 'creme', 'ivoire', 'taupe', 'sable', 'champagne',
]);

// Famille de couleur primaire → groupe de proximité visuelle
// Si combo veut "vert" et item est "vert", c'est un match parfait (même famille)
const COLOR_FAMILY_MAP = {
  'vert':    ['vert', 'kaki', 'olive', 'menthe', 'sapin', 'émeraude', 'pistache', 'amande', 'anis', 'bouteille'],
  'bleu':    ['bleu', 'marine', 'denim', 'ciel', 'azur', 'pétrole', 'turquoise', 'indigo', 'klein'],
  'rouge':   ['rouge', 'bordeaux', 'framboise', 'rouille', 'cerise', 'tomate', 'coquelicot', 'brique'],
  'rose':    ['rose', 'fuchsia', 'saumon', 'blush', 'poudré', 'dragée'],
  'marron':  ['marron', 'cognac', 'chocolat', 'caramel', 'tabac', 'moka', 'terracotta', 'camel', 'châtain'],
  'beige':   ['beige', 'sable', 'écru', 'champagne', 'taupe', 'crème', 'ivoire', 'ficelle', 'lin', 'nude'],
  'gris':    ['gris', 'souris', 'perle', 'anthracite', 'ardoise', 'étain', 'argent'],
  'jaune':   ['jaune', 'moutarde', 'citron', 'ocre', 'safran', 'paille', 'doré', 'miel'],
  'violet':  ['violet', 'lavande', 'prune', 'lilas', 'aubergine', 'parme', 'mauve'],
  'orange':  ['orange', 'corail', 'abricot', 'mandarine', 'cuivré'],
  'noir':    ['noir', 'charbon', 'jais'],
  'blanc':   ['blanc', 'ivoire', 'neige', 'cassé', 'optique'],
};

// Helper : retourne la famille d'une couleur quelconque (vert→vert, menthe→vert, etc.)
function getColorFamily(color) {
  if (!color) return null;
  const c = String(color).toLowerCase().trim();
  for (const [family, members] of Object.entries(COLOR_FAMILY_MAP)) {
    if (members.some(m => c.includes(m) || m.includes(c))) return family;
  }
  return null;
}

// ─── Score de match d'un item du dressing avec une "typical_piece" ───
// La banque décrit des pièces génériques ("chemise oxford blanche") et il
// faut trouver dans le dressing user les items qui correspondent au mieux.
//
// v1.10.92 : Score MULTI-CRITÈRES enrichi avec 7 facteurs.
//
//   Facteur               Poids   Source AI                Source Combo
//   ────────────────────────────────────────────────────────────────────
//   1. Keyword match      20%    name + brand            typical_piece
//   2. Style match        25%    styleScores.{style}     style
//   3. Occasion match     20%    occasions.{occ}         occasion
//   4. Color hiérarchique 15%    colorDetail (preferred) colors+colorDetails
//                                primaryColor (fallback)
//   5. Formality          10%    formality (0-10)        formality (0-10)
//   6. Temperature         5%    temperatureRange        temp_min/max
//   7. Season              5%    seasonality             season
//
//   FILTRES DURS (rejet immédiat) :
//   - formality différence > 5 points (gala vs plage)
//   - aucun overlap de température (item hiver pour combo été)
//   - season conflictuelle (item hiver only pour combo été only)
//
//   BONUS :
//   - +0.05 si versatility >= 8 (essentiels polyvalents jean noir, t-shirt blanc)
//   - +0.05 si colorDetail correspond exactement au combo.colorDetails
function scoreItemAgainstPiece(item, pieceDescription, comboContext, opts = {}) {
  if (!item || !pieceDescription) return 0;
  const ai = item.ai_metadata || {};
  const colorBank = opts.colorBank || null; // Phase C.4 v1.12.65
  const materialBank = opts.materialBank || null; // Phase D.3 v1.12.69

  // ═══ 0. FILTRES DURS (rejet immédiat si incompatibilité totale) ═══
  if (comboContext) {
    const targetStyle = (comboContext.style || '').toLowerCase().trim();
    const targetOcc = (comboContext.occasion || '').toLowerCase().trim();
    const itemStyleScores = ai.styleScores || {};
    const itemOccasions = ai.occasions || {};
    const hasStyleData = Object.keys(itemStyleScores).length > 0;
    const hasOccData = Object.keys(itemOccasions).length > 0;

    // ─── FILTRE STYLE MISMATCH (NOUVEAU v1.11.1) ───
    // Si combo cible un style et l'item a un styleScore CONCURRENT bien plus
    // élevé que le style cible, c'est un mismatch dur (ex: combo "chic" mais
    // item a sport=85 et chic=20 → rejet, même si on n'a pas formality propre).
    if (targetStyle && hasStyleData) {
      const possibleKeys = STYLE_KEY_MAP[targetStyle] || [targetStyle];
      // Score de l'item sur le style cible
      const onTargetScore = Math.max(...possibleKeys.map(k => itemStyleScores[k] || 0));

      // Score de l'item sur les styles "INCOMPATIBLES" avec le style cible
      const INCOMPATIBLE_STYLES = {
        'chic':         ['sport', 'sportif', 'streetwear', 'edgy'],
        'élégant':      ['sport', 'sportif', 'streetwear', 'edgy'],
        'workwear':     ['sport', 'sportif', 'streetwear', 'edgy'],
        'preppy':       ['sport', 'sportif', 'streetwear', 'edgy'],
        'classique':    ['sport', 'sportif', 'streetwear', 'edgy'],
        'minimaliste':  ['bohème', 'romantique'],
        'romantique':   ['sport', 'sportif', 'edgy', 'streetwear'],
        'bohème':       ['minimaliste', 'workwear', 'preppy'],
        'sport':        ['chic', 'élégant', 'romantique', 'workwear', 'preppy'],
        'sportif':      ['chic', 'élégant', 'romantique', 'workwear', 'preppy'],
        'streetwear':   ['chic', 'élégant', 'workwear', 'preppy', 'romantique'],
        'edgy':         ['chic', 'élégant', 'workwear', 'preppy', 'romantique'],
      };
      const incompatibleKeys = INCOMPATIBLE_STYLES[targetStyle] || [];
      const onIncompatibleScore = incompatibleKeys.length > 0
        ? Math.max(...incompatibleKeys.map(k => itemStyleScores[k] || 0))
        : 0;

      // Si le style INCOMPATIBLE est dominant ET le style cible est faible → rejet
      // Exemple : combo "chic", item baskets sport (sport=85, chic=20) → reject
      if (onIncompatibleScore >= 60 && onIncompatibleScore - onTargetScore >= 25) {
        return 0;
      }
      // Variant : si onTargetScore est très faible (< 20) et qu'on a des données fiables
      if (onTargetScore < 15 && Math.max(...Object.values(itemStyleScores)) >= 50) {
        return 0;
      }
    }

    // ─── FILTRE OCCASION MISMATCH (NOUVEAU v1.11.1) ───
    // Si combo cible "Soirée/Chic" et l'item a un score faible en occasion soirée
    // mais élevé en sport/quotidien → rejet
    if (targetOcc && hasOccData) {
      const possibleOccKeys = OCCASION_KEY_MAP[targetOcc] || [targetOcc];
      const onTargetOccScore = Math.max(...possibleOccKeys.map(k => itemOccasions[k] || 0));

      const INCOMPATIBLE_OCCASIONS = {
        'soirée':    ['sport'],
        'soiree':    ['sport'],
        'cocktail':  ['sport'],
        'cérémonie': ['sport'],
        'ceremonie': ['sport'],
        'bureau':    ['sport', 'plage'],
        'sport':     ['cérémonie', 'cocktail', 'soirée'],
        'chic':      ['sport'],
      };
      const incompatibleOccKeys = INCOMPATIBLE_OCCASIONS[targetOcc] || [];
      const onIncompatibleOccScore = incompatibleOccKeys.length > 0
        ? Math.max(...incompatibleOccKeys.map(k => itemOccasions[k] || 0))
        : 0;
      if (onIncompatibleOccScore >= 70 && onTargetOccScore < 30) {
        return 0;
      }
    }

    // ─── FILTRE FORMALITY (existant + inférence si manquant) ───
    let itemFormality = Number.isFinite(+ai.formality) ? +ai.formality : null;
    // v1.11.1 : si formality absent mais styleScores dispo, INFÉRER
    if (itemFormality === null && hasStyleData) {
      const chicScore = Math.max(itemStyleScores.chic || 0, itemStyleScores['élégant'] || 0);
      const sportScore = Math.max(itemStyleScores.sport || 0, itemStyleScores.sportif || 0);
      const workwearScore = Math.max(itemStyleScores.workwear || 0, itemStyleScores.preppy || 0);
      // Heuristique : workwear/chic dominant → formality 7-8
      // Sport dominant → formality 2-3
      // Casual moyen → formality 4-5
      if (workwearScore >= 60 || chicScore >= 70) itemFormality = 7;
      else if (chicScore >= 50) itemFormality = 6;
      else if (sportScore >= 60) itemFormality = 2;
      else if (sportScore >= 40) itemFormality = 3;
      else itemFormality = 5; // neutre
    }
    const comboFormality = Number.isFinite(+comboContext.formality) ? +comboContext.formality : null;
    if (itemFormality !== null && comboFormality !== null) {
      // Tolérance 4 points (au lieu de 5 avant) pour être plus strict
      if (Math.abs(itemFormality - comboFormality) > 4) return 0;
    }

    // Filtre température : pas d'overlap = rejet
    const itemTempRange = Array.isArray(ai.temperatureRange) && ai.temperatureRange.length === 2
      ? ai.temperatureRange : null;
    if (itemTempRange && Number.isFinite(+comboContext.temp_min) && Number.isFinite(+comboContext.temp_max)) {
      const [iMin, iMax] = itemTempRange;
      const overlap = Math.min(+comboContext.temp_max, iMax) - Math.max(+comboContext.temp_min, iMin);
      if (overlap < -5) return 0;
    }

    // Filtre saison
    const comboSeason = (comboContext.season || '').toLowerCase().trim();
    const itemSeasons = Array.isArray(ai.seasonality) ? ai.seasonality.map(s => String(s).toLowerCase()) : [];
    if (comboSeason && comboSeason !== 'toutes' && itemSeasons.length > 0 && itemSeasons.length < 3) {
      const matches = itemSeasons.some(s => s.includes(comboSeason) || comboSeason.includes(s));
      if (!matches) return 0;
    }
  }

  // ═══ 1. Keyword match (20%) ═══
  const desc = pieceDescription.toLowerCase();
  const itemText = [
    item.name || '',
    item.brand || '',
    ai.styleTags?.join(' ') || '',
    ai.material || '',
    item.color || '',
    ai.primaryColor || '',
    ai.colorDetail || '',
    ai.nameFr || '',
    ai.silhouette || '',
  ].join(' ').toLowerCase();

  let keywordScore = 0;
  if (itemText.trim()) {
    const stopWords = new Set([
      'un', 'une', 'le', 'la', 'les', 'de', 'du', 'des', 'en',
      'à', 'au', 'aux', 'et', 'ou', 'avec', 'sans', 'pour',
    ]);
    const keywords = desc.split(/\s+/)
      .map(w => w.trim().replace(/[.,;:]/g, ''))
      .filter(w => w.length > 2 && !stopWords.has(w));
    if (keywords.length > 0) {
      let matches = 0;
      for (const kw of keywords) {
        if (itemText.includes(kw)) matches++;
      }
      keywordScore = matches / keywords.length;
    }
  }

  if (!comboContext) return keywordScore;

  // ═══ 2. Style match (25%) ═══
  let styleScore = 0.5;
  const targetStyle = (comboContext.style || '').toLowerCase().trim();
  const itemStyleScores = ai.styleScores || {};
  if (targetStyle && Object.keys(itemStyleScores).length > 0) {
    const possibleKeys = STYLE_KEY_MAP[targetStyle] || [targetStyle];
    let bestMatchScore = 0;
    for (const key of possibleKeys) {
      const sc = itemStyleScores[key] || 0;
      if (sc > bestMatchScore) bestMatchScore = sc;
    }
    styleScore = Math.min(1, bestMatchScore / 100);
  }

  // ═══ 3. Occasion match (20%) — NOUVEAU ═══
  let occasionScore = 0.5;
  const targetOcc = (comboContext.occasion || '').toLowerCase().trim();
  const itemOccasions = ai.occasions || {};
  if (targetOcc && Object.keys(itemOccasions).length > 0) {
    const possibleOccKeys = OCCASION_KEY_MAP[targetOcc] || [targetOcc];
    let bestOccScore = 0;
    for (const key of possibleOccKeys) {
      const sc = itemOccasions[key] || 0;
      if (sc > bestOccScore) bestOccScore = sc;
    }
    occasionScore = Math.min(1, bestOccScore / 100);
  }

  // ═══ 4. Color match HIÉRARCHIQUE (15%) ═══
  // Priorité : colorDetail (nuance précise) > primaryColor (famille) > neutre
  // - Match parfait nuance (item.colorDetail ↔ combo.colorDetails) → 1.0
  // - Match famille (item.primaryColor ↔ combo.colors via family map) → 0.7
  // - Item neutre → 0.6 (passe-partout mais pas optimal)
  // - Mismatch → 0.2
  let colorScore = 0.5;
  let detailMatchBonus = 0; // +0.05 si match nuance parfaite
  const comboColors = Array.isArray(comboContext.colors) ? comboContext.colors.map(c => String(c).toLowerCase()) : [];
  const comboColorDetails = Array.isArray(comboContext.color_details || comboContext.colorDetails)
    ? (comboContext.color_details || comboContext.colorDetails).map(c => String(c).toLowerCase())
    : [];
  const itemDetail = (ai.colorDetail || '').toLowerCase().trim();
  const itemPrimary = (ai.primaryColor || item.color || '').toLowerCase().trim();
  const itemSecondaries = Array.isArray(ai.secondaryColors)
    ? ai.secondaryColors.map(c => String(c).toLowerCase()) : [];

  if (comboColors.length > 0 || comboColorDetails.length > 0) {
    // Tier 1 : MATCH NUANCE (le plus précis et privilégié)
    if (itemDetail && comboColorDetails.length > 0) {
      const detailMatches = comboColorDetails.some(cd =>
        cd === itemDetail || cd.includes(itemDetail) || itemDetail.includes(cd)
      );
      if (detailMatches) {
        colorScore = 1.0;
        detailMatchBonus = 0.05; // Bonus visibilité du match nuance
      }
    }

    // Tier 2 : MATCH FAMILLE (si nuance non matchée ou absente)
    if (colorScore < 1.0) {
      const itemFamily = getColorFamily(itemDetail) || getColorFamily(itemPrimary);
      if (itemFamily) {
        // Match si une couleur du combo est dans la même famille
        const familyMatches = comboColors.some(cc => {
          const cFamily = getColorFamily(cc);
          return cFamily === itemFamily;
        });
        if (familyMatches) colorScore = Math.max(colorScore, 0.75);
      }
    }

    // Tier 3 : NEUTRE (passe-partout mais pas optimal)
    if (colorScore < 0.75) {
      const isNeutral = NEUTRAL_COLORS.has(itemPrimary) ||
                        Array.from(NEUTRAL_COLORS).some(n => itemPrimary.includes(n));
      if (isNeutral) colorScore = Math.max(colorScore, 0.6);
    }

    // Tier 4 : MATCH SECONDAIRE (item a une couleur secondaire dans la palette combo)
    if (colorScore < 0.6 && itemSecondaries.length > 0) {
      const secMatch = itemSecondaries.some(sc => {
        const scFamily = getColorFamily(sc);
        return comboColors.some(cc => getColorFamily(cc) === scFamily);
      });
      if (secMatch) colorScore = Math.max(colorScore, 0.5);
    }

    // Tier 5 : MISMATCH explicite (couleur définie mais pas du tout dans la palette)
    if (colorScore === 0.5 && itemPrimary) {
      colorScore = 0.2;
    }
  }

  // ═══ 5. Formality match (10%) — NOUVEAU ═══
  let formalityScore = 0.7; // neutre élevé si données manquantes
  const itemFormality = Number.isFinite(+ai.formality) ? +ai.formality : null;
  const comboFormality = Number.isFinite(+comboContext.formality) ? +comboContext.formality : null;
  if (itemFormality !== null && comboFormality !== null) {
    const diff = Math.abs(itemFormality - comboFormality);
    // 0 diff → 1.0, diff 5 → 0
    formalityScore = Math.max(0, 1 - diff / 5);
  }

  // ═══ 6. Temperature match (5%) — NOUVEAU ═══
  let temperatureScore = 0.7;
  const itemTempRange = Array.isArray(ai.temperatureRange) && ai.temperatureRange.length === 2
    ? ai.temperatureRange : null;
  if (itemTempRange && Number.isFinite(+comboContext.temp_min) && Number.isFinite(+comboContext.temp_max)) {
    const [iMin, iMax] = itemTempRange;
    const cMin = +comboContext.temp_min;
    const cMax = +comboContext.temp_max;
    const overlap = Math.min(cMax, iMax) - Math.max(cMin, iMin);
    const comboRange = cMax - cMin;
    if (comboRange > 0) {
      // Score = ratio d'overlap par rapport au range du combo
      temperatureScore = Math.max(0, Math.min(1, overlap / comboRange));
    }
  }

  // ═══ 7. Season match (5%) — NOUVEAU ═══
  let seasonScore = 0.8; // neutre élevé si données manquantes
  const comboSeason = (comboContext.season || '').toLowerCase().trim();
  const itemSeasons = Array.isArray(ai.seasonality)
    ? ai.seasonality.map(s => String(s).toLowerCase()) : [];
  if (comboSeason && comboSeason !== 'toutes' && itemSeasons.length > 0) {
    const matches = itemSeasons.some(s => s.includes(comboSeason) || comboSeason.includes(s));
    seasonScore = matches ? 1.0 : 0.3;
  } else if (comboSeason === 'toutes' || itemSeasons.includes('toutes')) {
    seasonScore = 1.0;
  }

  // ═══ Bank color (Phase C.4 v1.12.65) ═══
  // Validation : la couleur de l'item est-elle dans les recettes
  // hebdo Claude pour sa (cat, sous-cat) ?
  let bankColorScore = 0.6;
  if (colorBank?.byKey) {
    try {
      const { scoreItemColor } = require('./ColorBankService');
      const r = scoreItemColor(item, colorBank);
      if (typeof r?.score === 'number') bankColorScore = r.score;
    } catch (e) {
      // ColorBankService pas disponible : on garde 0.6 neutre
    }
  }

  // ═══ Phase D.3 v1.12.69 — Bank material match ═══
  // Validation : la matière de l'item est-elle dans les recettes hebdo
  // Claude pour sa (cat, sous-cat) ?
  let bankMaterialScore = 0.6;
  if (materialBank?.byKey) {
    try {
      const { scoreItemMaterial } = require('./MaterialBankService');
      const r = scoreItemMaterial(item, materialBank);
      if (typeof r?.score === 'number') bankMaterialScore = r.score;
    } catch (e) {}
  }

  // ═══ v1.12.66 — colorAll fusion ═══
  const colorAll = 0.4 * colorScore + 0.6 * bankColorScore;

  // ═══ v1.12.69 — materialAll = bank only (combo n'a pas de field materials,
  // pair-wise pas applicable item ↔ combo description) ═══
  const materialAll = bankMaterialScore;

  // ═══ Score final pondéré (8 facteurs depuis v1.12.70 — Option A) ═══
  // keyword 13 + style 22 + occasion 18 + colorAll 17 + materialAll 17
  // + form 7 + temp 4 + season 2 = 100
  const finalScore =
      keywordScore     * 0.13
    + styleScore       * 0.22
    + occasionScore    * 0.18
    + colorAll         * 0.17
    + materialAll      * 0.17
    + formalityScore   * 0.07
    + temperatureScore * 0.04
    + seasonScore      * 0.02
    + detailMatchBonus;

  // ═══ Bonus polyvalence (max +0.05) ═══
  const versatility = Number.isFinite(+ai.versatility) ? +ai.versatility : 5;
  if (versatility >= 8) {
    return Math.min(1, finalScore + 0.05);
  }

  return Math.min(1, finalScore);
}

/**
 * Pour chaque combinaison de la banque, cherche les meilleurs items du
 * dressing qui correspondent. Retourne les combinaisons "matérialisables"
 * (au moins X items matchent).
 *
 * v1.9.38 : DIVERSITÉ DES ACCESSOIRES
 *   On suit l'usage GLOBAL des items entre combos. Quand un item (sac, bijou,
 *   etc.) a déjà été utilisé N fois, on le pénalise dans le scoring pour
 *   forcer le système à choisir d'autres items.
 *   Avant : tous les outfits avaient le même sac (top scorant pour tous les combos).
 *   Maintenant : les sacs/bijoux sont distribués entre les outfits.
 *
 * @param {Array} wardrobe items utilisateur
 * @param {Array} combinations sortie de getStyleBank
 * @param {object} opts { minMatchScore: 0.4, minPiecesMatched: 0.6 (60%) }
 * @returns {Array<{ combination, items, matchScore }>}
 */
export function findMatchingCombinations(wardrobe, combinations, opts = {}) {
  if (!Array.isArray(wardrobe) || !Array.isArray(combinations)) return [];
  const minMatchScore = opts.minMatchScore ?? 0.35;
  const minPiecesMatchedRatio = opts.minPiecesMatched ?? 0.6;

  // Index par catégorie pour accélérer
  const byCategory = {};
  for (const it of wardrobe) {
    if (!byCategory[it.category]) byCategory[it.category] = [];
    byCategory[it.category].push(it);
  }

  // ═══ v1.9.38 : Compteur d'usage GLOBAL des items entre combos ═══
  //   Pour chaque item utilisé, on incrémente son compteur.
  //   Au moment du scoring, on pénalise les items très utilisés pour favoriser
  //   ceux qui n'ont pas (ou peu) été choisis. → Diversité des accessoires.
  const globalUsageCount = new Map(); // itemId → nb d'utilisations

  const results = [];
  for (const combo of combinations) {
    const cats   = combo.categories || [];
    const pieces = combo.typical_pieces || [];
    if (cats.length !== pieces.length) continue;

    const matched = [];
    let totalScore = 0;
    let usedItemIds = new Set();

// Catégories "principales" (cœur de l'outfit) — exigence stricte sur le scoring
// Pour ces catégories, l'item doit vraiment matcher le style + couleur + occasion.
const CORE_CATEGORIES = new Set(['Hauts', 'Bas', 'Robes', 'Vestes', 'Chaussures']);

// Catégories "accessoires" (compléments) — exigence assouplie
// Le keyword match est moins fiable (un "collier doré" vs nom item "GUESS L.O.V.E.")
// On baisse le seuil minMatchScore pour ces catégories tout en gardant le filtrage style
const ACCESSORY_CATEGORIES = new Set(['Sacs', 'Bijoux', 'Accessoires']);

    for (let i = 0; i < cats.length; i++) {
      const cat = cats[i];
      const pieceDesc = pieces[i];
      const candidates = byCategory[cat] || [];
      let bestItem = null;
      let bestScore = 0;
      for (const cand of candidates) {
        if (usedItemIds.has(cand.id)) continue;
        // v1.10.92 : scoring multi-critères (7 facteurs + filtres durs)
        const baseScore = scoreItemAgainstPiece(cand, pieceDesc, combo, {
          colorBank: opts.colorBank,
          materialBank: opts.materialBank, // Phase D.3 v1.12.69
        });
        // ═══ v1.9.38 : Pénalité d'usage répété ═══
        //   Plus un item a été utilisé dans des outfits précédents, plus on
        //   réduit son score. Pénalité : -10% par utilisation, max -50%.
        //   Force le système à choisir des accessoires variés.
        const usage = globalUsageCount.get(cand.id) || 0;
        const usagePenalty = Math.min(0.5, usage * 0.1);
        const score = baseScore * (1 - usagePenalty);
        if (score > bestScore) {
          bestScore = score;
          bestItem = cand;
        }
      }

      // v1.10.92 : SEUIL DIFFÉRENCIÉ par catégorie
      // Les accessoires (sacs/bijoux/accessoires) ont un keyword match moins
      // fiable (description courte) → on baisse le seuil pour eux. Les bijoux
      // sont les moins descriptibles → seuil encore plus bas.
      // Le filtrage style/occasion/couleur du score reste cohérent dans tous les cas.
      let categoryThreshold = minMatchScore;
      if (cat === 'Bijoux') categoryThreshold = Math.max(0.20, minMatchScore - 0.15);
      else if (ACCESSORY_CATEGORIES.has(cat)) categoryThreshold = Math.max(0.25, minMatchScore - 0.10);

      if (bestItem && bestScore >= categoryThreshold) {
        matched.push({ item: bestItem, score: bestScore, pieceDesc, category: cat });
        usedItemIds.add(bestItem.id);
        totalScore += bestScore;
        globalUsageCount.set(bestItem.id, (globalUsageCount.get(bestItem.id) || 0) + 1);
      }
    }

    const ratio = matched.length / pieces.length;
    if (ratio >= minPiecesMatchedRatio) {
      // v1.10.92 : Vérifier la cohérence stylistique GLOBALE de l'outfit
      // Utilise STYLE_KEY_MAP global (cohérence avec le scoring item)
      const matchedItems = matched.map(m => m.item);
      const targetStyle = (combo.style || '').toLowerCase().trim();
      let styleCoherenceBonus = 0;
      let styleCoherenceWarning = false;

      if (targetStyle && matchedItems.length >= 2) {
        const possibleKeys = STYLE_KEY_MAP[targetStyle] || [targetStyle];
        let itemsMatchingStyle = 0;
        let itemsWithStyleData = 0;
        for (const m of matched) {
          const it = m.item;
          const ss = it.ai_metadata?.styleScores || {};
          if (Object.keys(ss).length === 0) continue;
          // v1.10.92 : Les accessoires ont un poids de moitié dans le check cohérence
          // (un sac peut servir avec plusieurs styles, on est plus permissif)
          const weight = ACCESSORY_CATEGORIES.has(m.category) ? 0.5 : 1.0;
          itemsWithStyleData += weight;
          const maxOnTarget = Math.max(...possibleKeys.map(k => ss[k] || 0));
          if (maxOnTarget >= 40) itemsMatchingStyle += weight;
        }
        if (itemsWithStyleData > 0) {
          const styleRatio = itemsMatchingStyle / itemsWithStyleData;
          if (styleRatio >= 0.5) styleCoherenceBonus = 0.15;
          else if (styleRatio < 0.25) styleCoherenceWarning = true;
        }
      }

      if (styleCoherenceWarning) continue;

      results.push({
        combination: combo,
        items: matchedItems,
        matchScore: (totalScore / pieces.length) + styleCoherenceBonus,
        piecesMatched: matched.length,
        piecesTotal: pieces.length,
      });
    }
  }

  // Trier par score puis par tendance
  results.sort((a, b) => {
    const scoreDiff = b.matchScore - a.matchScore;
    if (Math.abs(scoreDiff) > 0.05) return scoreDiff;
    return (b.combination.trend_score || 0) - (a.combination.trend_score || 0);
  });

  return results;
}
